import React from "react";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  Stack,
  Typography,
  IconButton,
  Tooltip,
  Divider,
  Container,
} from "@mui/material";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Icons
import SettingsIcon from "@mui/icons-material/Settings";
import IntegrationIcon from "@mui/icons-material/IntegrationInstructions";
import BillingIcon from "@mui/icons-material/Receipt";
import UsersIcon from "@mui/icons-material/Group";
import DescriptionIcon from "@mui/icons-material/Description";
import RuleIcon from "@mui/icons-material/Rule";

import { useApp } from "contexts/AppContext";

const Settings = () => {
  const { t } = useTranslation();
  const isCostiLockEnabled = process.env.REACT_APP_COSTI_LOCK === "true";
  // check if user is admin
  const app = useApp();

  const settingsOptions = [
    {
      path: "/settings/organization",
      label: t("settings.overview.cards.organization.title"),
      description: t("settings.overview.cards.organization.description"),
      icon: <SettingsIcon sx={{ fontSize: 32 }} />,
    },
    ...(!isCostiLockEnabled
      ? [
          {
            path: "/settings/billing",
            label: t("settings.overview.cards.billing.title"),
            description: t("settings.overview.cards.billing.description"),
            icon: <BillingIcon sx={{ fontSize: 32 }} />,
          },
        ]
      : []),
    {
      path: "/settings/users",
      label: t("settings.overview.cards.users.title"),
      description: t("settings.overview.cards.users.description"),
      icon: <UsersIcon sx={{ fontSize: 32 }} />,
    },
  ];

  return (
    <Box className={"container mainContainer docsContainer "}>
      <Box
        sx={{
          minHeight: "100vh",
          background: "#fff", // updated to white background
          py: 4,
          px: { xs: 2, md: 4 },
        }}
      >
        <Container maxWidth="lg">
          <div className="headerPush"></div>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h2">{t("settings.overview.title")}</Typography>
          </Stack>
          <Divider sx={{ mt: 1, mb: 3 }} />

          <Grid container spacing={3} justifyContent="flex-start">
            {settingsOptions.map((option) => (
              <Grid item xs={12} sm={4} md={4} lg={4} key={option.path}>
                <Card
                  sx={{
                    borderRadius: 2,
                    boxShadow: 2,
                    transition: "transform 0.2s, box-shadow 0.2s",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: 4,
                    },
                    height: "100%",
                  }}
                >
                  <CardActionArea
                    component={Link}
                    to={option.path}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      minHeight: 260, // ensures a consistent card height
                      p: 2,
                    }}
                  >
                    <Box
                      sx={{
                        mb: 2,
                        width: 60,
                        height: 60,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "background.paper",
                        boxShadow: 1,
                      }}
                    >
                      <Tooltip title={option.label} placement="top">
                        <IconButton
                          disableRipple
                          sx={{ "&:hover": { backgroundColor: "transparent" } }}
                        >
                          {option.icon}
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <CardContent sx={{ textAlign: "center" }}>
                      <Typography
                        variant="h3"
                        sx={{ fontWeight: 600, mb: 0.5 }}
                        gutterBottom
                      >
                        {option.label}
                      </Typography>
                      {option.description && (
                        <Typography variant="body2" color="text.secondary">
                          {option.description}
                        </Typography>
                      )}
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default Settings;
